/**
 * AdminAuditPage - cross-ticket audit search over ticket_events (12.4).
 *
 * Same layout shape as AdminLogsPage but no auto-refresh: audit history
 * is append-only and an investigator wants a stable snapshot. Filters
 * are debounced 300 ms.
 */
import { useEffect, useMemo, useState } from "react";

import { Alert, Button, Card, Input, Select, Spinner } from "@/components/ui";
import { AuditEventRow } from "@/components/admin/AuditEventRow";
import { useAdminAuditEvents } from "@/hooks/useAdminAuditEvents";
import { usePageTitle } from "@/hooks/usePageTitle";
import api from "@/lib/api";
import type {
  ActorKind,
  TicketEventQuery,
  TicketEventResponse,
} from "@/types/api";

const ACTOR_KIND_OPTIONS = [
  { value: "", label: "Any actor kind" },
  { value: "user", label: "user" },
  { value: "api_key", label: "api_key" },
  { value: "email_inbound", label: "email_inbound" },
  { value: "system", label: "system" },
  { value: "import", label: "import" },
];

interface FilterState {
  ticket_id: string;
  actor_user_id: string;
  actor_kind: "" | ActorKind;
  event_type: string;
  since: string;
  until: string;
}

const EMPTY_FILTERS: FilterState = {
  ticket_id: "",
  actor_user_id: "",
  actor_kind: "",
  event_type: "",
  since: "",
  until: "",
};

function toQuery(f: FilterState): TicketEventQuery {
  const q: TicketEventQuery = {};
  if (f.ticket_id.trim()) q.ticket_id = f.ticket_id.trim();
  if (f.actor_user_id.trim()) q.actor_user_id = f.actor_user_id.trim();
  if (f.actor_kind) q.actor_kind = f.actor_kind;
  if (f.event_type.trim()) q.event_type = f.event_type.trim();
  if (f.since) q.since = new Date(f.since).toISOString();
  if (f.until) q.until = new Date(f.until).toISOString();
  return q;
}

export function AdminAuditPage(): JSX.Element {
  usePageTitle("Audit log");

  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [debounced, setDebounced] = useState<FilterState>(EMPTY_FILTERS);
  const [olderRows, setOlderRows] = useState<TicketEventResponse[]>([]);
  const [olderError, setOlderError] = useState<string | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(filters), 300);
    return () => clearTimeout(id);
  }, [filters]);

  useEffect(() => {
    setOlderRows([]);
    setOlderError(null);
  }, [debounced]);

  const query = useMemo(() => toQuery(debounced), [debounced]);
  const { data, isLoading, error, refetch, isFetching } = useAdminAuditEvents({
    query,
  });

  const rows = useMemo(() => {
    const head = data ?? [];
    return [...head, ...olderRows];
  }, [data, olderRows]);

  const handleLoadOlder = async () => {
    if (rows.length === 0) return;
    const tail = rows[rows.length - 1];
    if (!tail) return;
    setLoadingOlder(true);
    setOlderError(null);
    try {
      const response = await api.get<TicketEventResponse[]>("/admin/audit", {
        params: { ...query, before_id: tail.id },
      });
      setOlderRows((prev) => [...prev, ...response.data]);
    } catch (err) {
      setOlderError(
        err instanceof Error ? err.message : "Failed to load older events.",
      );
    } finally {
      setLoadingOlder(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-7xl">
      <header className="mb-4 flex items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900">
            Audit log
          </h1>
          <p className="mt-1 text-sm text-neutral-600">
            Cross-ticket ticket_event search. Useful for "who marked this
            ticket spam" investigations.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void refetch()}
          isLoading={isFetching}
        >
          Refresh
        </Button>
      </header>

      <Card padding="sm" className="mb-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
          <Input
            id="audit-filter-ticket-id"
            label="Ticket id"
            value={filters.ticket_id}
            placeholder="uuid"
            onChange={(e) =>
              setFilters((f) => ({ ...f, ticket_id: e.target.value }))
            }
          />
          <Input
            id="audit-filter-actor-user-id"
            label="Actor user id"
            value={filters.actor_user_id}
            placeholder="uuid"
            onChange={(e) =>
              setFilters((f) => ({ ...f, actor_user_id: e.target.value }))
            }
          />
          <Select
            id="audit-filter-actor-kind"
            label="Actor kind"
            options={ACTOR_KIND_OPTIONS}
            value={filters.actor_kind}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                actor_kind: e.target.value as "" | ActorKind,
              }))
            }
          />
          <Input
            id="audit-filter-event-type"
            label="Event type"
            value={filters.event_type}
            placeholder="status_changed"
            onChange={(e) =>
              setFilters((f) => ({ ...f, event_type: e.target.value }))
            }
          />
          <Input
            id="audit-filter-since"
            label="Since"
            type="datetime-local"
            value={filters.since}
            onChange={(e) =>
              setFilters((f) => ({ ...f, since: e.target.value }))
            }
          />
          <Input
            id="audit-filter-until"
            label="Until"
            type="datetime-local"
            value={filters.until}
            onChange={(e) =>
              setFilters((f) => ({ ...f, until: e.target.value }))
            }
          />
        </div>
        <div className="mt-3 flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setFilters(EMPTY_FILTERS)}
          >
            Clear filters
          </Button>
        </div>
      </Card>

      {error && (
        <Alert variant="error" title="Failed to load audit events" className="mb-4">
          {error instanceof Error ? error.message : "Unknown error."}
        </Alert>
      )}

      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Timestamp
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Ticket
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Actor
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Kind
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Event
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Change
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 bg-white">
              {isLoading && (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center">
                    <Spinner size="md" />
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-3 py-8 text-center text-sm text-neutral-500"
                  >
                    No events match this filter.
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <AuditEventRow key={row.id} row={row} />
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="mt-4 flex flex-col items-center gap-2">
        {olderError && (
          <Alert variant="error" title="Failed to load older events">
            {olderError}
          </Alert>
        )}
        {rows.length > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void handleLoadOlder()}
            isLoading={loadingOlder}
          >
            Load older
          </Button>
        )}
      </div>
    </div>
  );
}

export default AdminAuditPage;
